import * as React from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { CircleSlash, Eye, FileUp, Paperclip, PencilLine, Plus } from 'lucide-react';

import { api } from '@/lib/api';
import { INTERVALO_ATUALIZACAO_MS } from '@/lib/consultas';
import { formatarData } from '@/lib/utils';
import type { RespostaPaginada } from '@/components/paginacao';
import type { SolicitacaoLista } from '@/features/solicitacoes/tipos';
import {
  ROTULOS_TIPO,
  STATUS,
  TIPOS_SOLICITACAO,
  VALOR_STATUS_ABERTOS,
  obterDefinicaoStatus,
  type StatusSolicitacao,
} from '@/features/solicitacoes/status';
import type { Especialidade, UbsResumida } from '@/features/admin/tipos';
import { useAuth } from '@/features/auth/contexto';
import {
  ORIGENS,
  ROTULOS_ORIGEM,
  ehDemandaReprimida,
  podeRegistrarDemandaReprimida,
  podeRegistrarSolicitacao,
  type OrigemSolicitacao,
} from '@/features/solicitacoes/origem';
import { CabecalhoPagina } from '@/components/cabecalho-pagina';
import { IndicadorAtualizacao } from '@/components/indicador-atualizacao';
import { BarraBusca } from '@/components/barra-busca';
import { Paginacao } from '@/components/paginacao';
import { StatusBadge } from '@/components/status-badge';
import { BadgeDocumentoNovo } from '@/components/badge-documento-novo';
import { BadgeEdicaoNova } from '@/components/badge-edicao-nova';
import { BadgeCancelamentoPedido } from '@/components/badge-cancelamento-pedido';
import { BadgePrioridade } from '@/components/badge-prioridade';
import { BadgeOrigem } from '@/components/badge-origem';
import { Botao } from '@/components/ui/button';
import { Selecao } from '@/components/ui/input';
import {
  Tabela,
  TabelaCabecalho,
  TabelaCelula,
  TabelaCorpo,
  TabelaLinha,
  TabelaTitulo,
  TabelaVazia,
} from '@/components/ui/table';
import { EsqueletoTabela } from '@/components/ui/skeleton';

const COLUNAS = 7;

interface PaginaListaSolicitacoesProps {
  /**
   * Quando presente, a tela é a fila daquela origem: o filtro some e a consulta
   * sai travada.
   *
   * Uma página só, parametrizada, e não uma segunda quase igual. São a mesma
   * tabela, os mesmos oito filtros, a mesma paginação e o mesmo polling —
   * duplicar significaria consertar cada um deles duas vezes.
   */
  origemFixa?: OrigemSolicitacao;
}

export function PaginaListaSolicitacoes({ origemFixa }: PaginaListaSolicitacoesProps = {}) {
  const { usuario } = useAuth();
  const ehUbs = usuario?.perfil === 'UBS';
  // O aviso de documento novo é dirigido a quem reavalia o processo.
  const ehRegulacao = usuario?.perfil === 'REGULACAO';

  // Os filtros vivem na URL: recarregar a página ou compartilhar o link
  // preserva o que estava sendo visto.
  const [parametrosUrl, setParametrosUrl] = useSearchParams();

  const pagina = Number(parametrosUrl.get('pagina') ?? '1');
  const busca = parametrosUrl.get('busca') ?? '';
  const status = parametrosUrl.get('status') ?? '';
  const tipo = parametrosUrl.get('tipo') ?? '';
  const especialidadeId = parametrosUrl.get('especialidade') ?? '';
  const ubsId = parametrosUrl.get('ubs') ?? '';
  const soDocumentoNovo = parametrosUrl.get('documentoNovo') === '1';
  const soEdicaoNova = parametrosUrl.get('editada') === '1';
  const soCancelamentoPedido = parametrosUrl.get('cancelamento') === '1';
  // Na fila dedicada o padrão é a espera: é ela que diz quem está há mais
  // tempo parado, que é a pergunta que a demanda reprimida levanta.
  const ordenarPor = parametrosUrl.get('ordenarPor') ?? 'esperaDesde';
  const origem = origemFixa ?? parametrosUrl.get('origem') ?? '';
  const soDemandaReprimida = Boolean(origemFixa && ehDemandaReprimida(origemFixa));

  const atualizar = React.useCallback(
    (chave: string, valor: string) => {
      setParametrosUrl((atuais) => {
        const novos = new URLSearchParams(atuais);
        if (valor) novos.set(chave, valor);
        else novos.delete(chave);
        // Trocar um filtro sempre volta à primeira página.
        if (chave !== 'pagina') novos.delete('pagina');
        return novos;
      });
    },
    [setParametrosUrl],
  );

  const consulta = new URLSearchParams({ pagina: String(pagina), limite: '20', ordenarPor });
  if (busca) consulta.set('busca', busca);
  if (status) consulta.set('status', status);
  if (tipo) consulta.set('tipo', tipo);
  if (especialidadeId) consulta.set('especialidadeId', especialidadeId);
  if (ubsId) consulta.set('ubsId', ubsId);
  if (soDocumentoNovo) consulta.set('comDocumentoNovo', 'true');
  if (soEdicaoNova) consulta.set('comEdicaoNova', 'true');
  if (soCancelamentoPedido) consulta.set('comCancelamentoPedido', 'true');
  if (origem) consulta.set('origem', origem);

  const { data, isPending, isFetching } = useQuery({
    queryKey: ['solicitacoes', consulta.toString()],
    queryFn: () => api.get<RespostaPaginada<SolicitacaoLista>>(`/solicitacoes?${consulta}`),
    refetchInterval: INTERVALO_ATUALIZACAO_MS,
    // Mantém a página anterior visível enquanto a nova carrega: sem isso, o
    // polling faria a tabela piscar a cada ciclo.
    placeholderData: (anterior) => anterior,
  });

  const { data: especialidades } = useQuery({
    queryKey: ['especialidades-ativas'],
    queryFn: () => api.get<Pick<Especialidade, 'id' | 'nome'>[]>('/especialidades/ativas'),
    staleTime: 5 * 60_000,
  });

  const { data: unidades } = useQuery({
    queryKey: ['ubs-ativas'],
    queryFn: () => api.get<UbsResumida[]>('/ubs/ativas'),
    staleTime: 5 * 60_000,
    enabled: !ehUbs,
  });

  const temFiltro = Boolean(
    busca ||
    status ||
    tipo ||
    especialidadeId ||
    ubsId ||
    soDocumentoNovo ||
    soEdicaoNova ||
    soCancelamentoPedido ||
    // A origem travada da tela dedicada não conta: senão "Limpar filtros"
    // ficaria aceso para sempre, sem nada para limpar.
    (!origemFixa && origem),
  );

  return (
    <>
      <CabecalhoPagina
        titulo={soDemandaReprimida ? 'Demanda reprimida' : 'Solicitações'}
        descricao={
          soDemandaReprimida
            ? ehUbs
              ? 'Pedidos da sua unidade anteriores ao KAVI, registrados pela Regulação.'
              : 'Pedidos anteriores ao KAVI, registrados a partir do estoque da secretaria.'
            : ehUbs
              ? 'Solicitações registradas pela sua unidade.'
              : usuario?.perfil === 'GESTOR'
                ? 'Solicitações das unidades que você acompanha.'
                : 'Solicitações de todas as unidades.'
        }
        acoes={
          <div className="flex items-center gap-3">
            <IndicadorAtualizacao buscando={isFetching} />
            {soDemandaReprimida
              ? usuario &&
                podeRegistrarDemandaReprimida(usuario.perfil) && (
                  <Botao asChild>
                    <Link to="/demanda-reprimida/nova">
                      <Plus aria-hidden="true" />
                      Registrar demanda
                    </Link>
                  </Botao>
                )
              : usuario &&
                podeRegistrarSolicitacao(usuario.perfil) && (
                  <Botao asChild>
                    <Link to="/solicitacoes/nova">
                      <Plus aria-hidden="true" />
                      Nova solicitação
                    </Link>
                  </Botao>
                )}
          </div>
        }
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <BarraBusca
          valor={busca}
          aoMudar={(valor) => atualizar('busca', valor)}
          placeholder="Protocolo, nome do paciente ou CPF"
        />

        <Selecao
          value={status}
          onChange={(evento) => atualizar('status', evento.target.value)}
          aria-label="Filtrar por status"
          className="w-full sm:w-56"
        >
          <option value="">Todos os status</option>
          {/* Recorte de vários status num valor só, para o cartão do painel
              abrir a lista no mesmo número que ele mostra — e com o filtro à
              vista, em vez de aplicado em silêncio. */}
          <option value={VALOR_STATUS_ABERTOS}>Em aberto</option>
          {STATUS.map((item) => (
            <option key={item} value={item}>
              {obterDefinicaoStatus(item as StatusSolicitacao).rotulo}
            </option>
          ))}
        </Selecao>

        <Selecao
          value={tipo}
          onChange={(evento) => atualizar('tipo', evento.target.value)}
          aria-label="Filtrar por tipo"
          className="w-full sm:w-40"
        >
          <option value="">Todos os tipos</option>
          {TIPOS_SOLICITACAO.map((item) => (
            <option key={item} value={item}>
              {ROTULOS_TIPO[item]}
            </option>
          ))}
        </Selecao>

        {!origemFixa && (
          <Selecao
            value={origem}
            onChange={(evento) => atualizar('origem', evento.target.value)}
            aria-label="Filtrar por origem"
            className="w-full sm:w-48"
          >
            <option value="">Todas as origens</option>
            {ORIGENS.map((item) => (
              <option key={item} value={item}>
                {ROTULOS_ORIGEM[item]}
              </option>
            ))}
          </Selecao>
        )}

        <Selecao
          value={especialidadeId}
          onChange={(evento) => atualizar('especialidade', evento.target.value)}
          aria-label="Filtrar por especialidade"
          className="w-full sm:w-48"
        >
          <option value="">Todas as especialidades</option>
          {especialidades?.map((item) => (
            <option key={item.id} value={item.id}>
              {item.nome}
            </option>
          ))}
        </Selecao>

        {ehRegulacao && (
          <Botao
            variante={soDocumentoNovo ? 'primario' : 'contorno'}
            onClick={() => atualizar('documentoNovo', soDocumentoNovo ? '' : '1')}
            aria-pressed={soDocumentoNovo}
            title="Solicitações que receberam documento da UBS e ainda não foram abertas"
          >
            <FileUp aria-hidden="true" />
            Documento novo
          </Botao>
        )}

        {ehRegulacao && (
          <Botao
            variante={soEdicaoNova ? 'primario' : 'contorno'}
            onClick={() => atualizar('editada', soEdicaoNova ? '' : '1')}
            aria-pressed={soEdicaoNova}
            title="Solicitações corrigidas depois da leitura e ainda não reabertas"
          >
            <PencilLine aria-hidden="true" />
            Dados corrigidos
          </Botao>
        )}

        {/* Também para a UBS: é ela que acompanha o pedido que fez. */}
        <Botao
          variante={soCancelamentoPedido ? 'primario' : 'contorno'}
          onClick={() => atualizar('cancelamento', soCancelamentoPedido ? '' : '1')}
          aria-pressed={soCancelamentoPedido}
          title="Solicitações com pedido de cancelamento aguardando decisão"
        >
          <CircleSlash aria-hidden="true" />
          Cancelamento pedido
        </Botao>

        {!ehUbs && (
          <Selecao
            value={ubsId}
            onChange={(evento) => atualizar('ubs', evento.target.value)}
            aria-label="Filtrar por UBS"
            className="w-full sm:w-48"
          >
            <option value="">Todas as UBS</option>
            {unidades?.map((item) => (
              <option key={item.id} value={item.id}>
                {item.nome}
              </option>
            ))}
          </Selecao>
        )}

        <Selecao
          value={ordenarPor}
          onChange={(evento) => atualizar('ordenarPor', evento.target.value)}
          aria-label="Ordenar por"
          className="w-full sm:w-44"
        >
          <option value="esperaDesde">Mais tempo na fila</option>
          <option value="criadoEm">Cadastradas há mais tempo</option>
          <option value="prioridade">Prioridade</option>
          <option value="protocolo">Protocolo</option>
          <option value="pacienteNome">Nome do paciente</option>
        </Selecao>

        {temFiltro && (
          <Botao variante="fantasma" tamanho="sm" onClick={() => setParametrosUrl({})}>
            Limpar filtros
          </Botao>
        )}
      </div>

      <Tabela>
        <TabelaCabecalho>
          <TabelaLinha>
            <TabelaTitulo>Protocolo</TabelaTitulo>
            <TabelaTitulo>Paciente</TabelaTitulo>
            <TabelaTitulo>Especialidade</TabelaTitulo>
            {!ehUbs && <TabelaTitulo>UBS</TabelaTitulo>}
            <TabelaTitulo>Prioridade</TabelaTitulo>
            <TabelaTitulo>Status</TabelaTitulo>
            <TabelaTitulo className="text-right">Ações</TabelaTitulo>
          </TabelaLinha>
        </TabelaCabecalho>
        <TabelaCorpo>
          {isPending && <EsqueletoTabela colunas={ehUbs ? COLUNAS - 1 : COLUNAS} />}

          {!isPending && data?.dados.length === 0 && (
            <TabelaVazia colunas={ehUbs ? COLUNAS - 1 : COLUNAS}>
              {temFiltro
                ? 'Nenhuma solicitação corresponde aos filtros selecionados.'
                : soDemandaReprimida
                  ? ehUbs
                    ? 'Nenhuma demanda reprimida registrada para a sua unidade.'
                    : 'Nenhuma demanda reprimida registrada.'
                  : ehUbs
                    ? 'Sua unidade ainda não registrou solicitações.'
                    : 'Nenhuma solicitação registrada.'}
            </TabelaVazia>
          )}

          {data?.dados.map((solicitacao) => (
            <TabelaLinha key={solicitacao.id}>
              <TabelaCelula rotulo="Protocolo">
                <Link
                  to={`/solicitacoes/${solicitacao.id}`}
                  className="text-marca font-mono text-sm font-medium hover:underline"
                >
                  {solicitacao.protocolo}
                </Link>
                <div className="text-silenciado-texto text-xs">
                  {ehDemandaReprimida(solicitacao.origem) ? (
                    <span title="Desde quando o paciente espera — é por aqui que a fila ordena.">
                      Na fila desde {formatarData(solicitacao.esperaDesde)}
                    </span>
                  ) : (
                    formatarData(solicitacao.criadoEm)
                  )}
                </div>
                {/* Depois do link: o protocolo continua sendo o primeiro alvo
                    de tabulação da linha. Fora da tela dedicada, onde o selo
                    seria o mesmo em todas as linhas. */}
                {!origemFixa && (
                  <BadgeOrigem origem={solicitacao.origem} tamanho="sm" className="mt-1" />
                )}
              </TabelaCelula>

              <TabelaCelula rotulo="Paciente">
                <div className="font-medium">{solicitacao.pacienteNome}</div>
                <div className="text-silenciado-texto flex items-center gap-1.5 text-xs">
                  {ROTULOS_TIPO[solicitacao.tipo]}
                  {solicitacao._count.anexos > 0 && (
                    <span className="flex items-center gap-0.5">
                      <Paperclip className="size-3" aria-hidden="true" />
                      {solicitacao._count.anexos}
                      <span className="apenas-leitor-tela"> anexo(s)</span>
                    </span>
                  )}
                </div>
              </TabelaCelula>

              <TabelaCelula rotulo="Especialidade" className="text-silenciado-texto text-sm">
                {solicitacao.especialidade.nome}
              </TabelaCelula>

              {!ehUbs && (
                <TabelaCelula rotulo="UBS" className="text-silenciado-texto text-sm">
                  {solicitacao.ubs.nome}
                </TabelaCelula>
              )}

              <TabelaCelula rotulo="Prioridade">
                <BadgePrioridade prioridade={solicitacao.prioridade} />
              </TabelaCelula>

              <TabelaCelula rotulo="Status">
                <div className="flex flex-wrap items-center gap-1.5">
                  <StatusBadge status={solicitacao.status} tamanho="sm" />
                  {/* Anexar não muda status: sem este selo, o documento entra
                      no processo sem ninguém da Regulação saber. Só para a
                      Regulação — para a UBS seria avisá-la do que ela mesma
                      acabou de fazer. */}
                  {ehRegulacao && solicitacao.documentoNovoEm && (
                    <BadgeDocumentoNovo tamanho="sm" />
                  )}
                  {/* Editar também não muda status. Só para a Regulação, e
                      pela mesma razão: quem corrigiu já sabe o que corrigiu. */}
                  {ehRegulacao && solicitacao.edicaoNovaEm && <BadgeEdicaoNova tamanho="sm" />}
                  {/* Este selo aparece para todos os perfis: a UBS precisa
                      acompanhar o pedido que fez, e quem decide precisa achá-lo
                      na fila. */}
                  {solicitacao.cancelamentoSolicitadoEm && <BadgeCancelamentoPedido tamanho="sm" />}
                </div>
              </TabelaCelula>

              <TabelaCelula className="text-right">
                <Botao variante="fantasma" tamanho="sm" asChild>
                  <Link
                    to={`/solicitacoes/${solicitacao.id}`}
                    aria-label={`Abrir protocolo ${solicitacao.protocolo}`}
                  >
                    <Eye aria-hidden="true" />
                    Abrir
                  </Link>
                </Botao>
              </TabelaCelula>
            </TabelaLinha>
          ))}
        </TabelaCorpo>
      </Tabela>

      {data && <Paginacao meta={data.meta} aoMudarPagina={(p) => atualizar('pagina', String(p))} />}
    </>
  );
}
